import React, { useState, useEffect } from "react";
import { Modal, Box } from "@mui/material";
import { getBranchDetails, pauseOnlineOrderRestaurantV2 } from "../../../../../config/Services";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";
import "../../../ordersPage/OrdersPage.css";

const pauseModalStyle = {
  backgroundColor: "#fff",
  color: "black",
  position: "fixed",
  top: 0,
  right: 0,
  width: "100%",
  maxWidth: "520px",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 24px 80px rgba(15, 23, 42, 0.18)",
  borderLeft: "1px solid rgba(226, 232, 240, 0.8)",
  outline: "none",
};

const platformOptions = [
  {
    value: "zomato",
    label: "Zomato",
    icon: <div className="platform-icon-letter platform-zomato">Z</div>,
  },
  {
    value: "swiggy",
    label: "Swiggy",
    icon: <div className="platform-icon-letter platform-swiggy">S</div>,
  },
  {
    value: "ubereats",
    label: "UberEats",
    icon: <div className="platform-icon-letter platform-ubereats">U</div>,
  },
  {
    value: "website",
    label: "Website",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="platform-svg platform-website"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
        <path d="M2 12h20" />
      </svg>
    ),
  },
  {
    value: "whatsapp",
    label: "WhatsApp",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="platform-svg platform-whatsapp"
        aria-hidden="true"
      >
        <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
      </svg>
    ),
  },
  {
    value: "magicpin",
    label: "MagicPin",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="platform-svg platform-magicpin"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
        <path d="M2 12h20" />
      </svg>
    ),
  },
];

const PauseOnlineOrdersModal = ({ open, onClose }) => {
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [activePlatforms, setActivePlatforms] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [platformSyncStatus, setPlatformSyncStatus] = useState({});
  const [pauseOrderLoader, setPauseOrderLoader] = useState(null);

  const showSnackbar = useSnackbar();
  const locId = localStorage.getItem("locId");

  const fetchPlatformStatus = () => {
    getBranchDetails(locId)
      .then((res) => {
        const platformStatus = res?.data?.branch?.storeTogglePlatformStatus || {};
        const syncStatus = res?.data?.branch?.platformStatusSync || {};

        const enabledPlatforms = Object.keys(platformStatus)
          ?.filter((p) => platformStatus[p] === "ENABLED")
          ?.map((p) => p.toLowerCase());

        const formattedPlatforms = Object.keys(platformStatus)?.map((p) => p?.toLowerCase());
        const formattedSyncStatus = {};
        Object.keys(syncStatus)?.forEach((key) => {
          formattedSyncStatus[key.toLowerCase()] = syncStatus[key];
        });

        setAvailablePlatforms(formattedPlatforms);
        setSelectedPlatforms(enabledPlatforms);
        setActivePlatforms(enabledPlatforms);
        setPlatformSyncStatus(formattedSyncStatus);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (open) {
      fetchPlatformStatus();
    }
  }, [open]);

  const updatePlatformStatus = (enabledPlatforms = [], actionType) => {
    setPauseOrderLoader(actionType);

    const storeTogglePlatformStatus = {};

    platformOptions
      ?.filter((p) => availablePlatforms?.includes(p?.value))
      ?.forEach((platform) => {
        storeTogglePlatformStatus[platform?.value] = enabledPlatforms?.includes(platform?.value)
          ? "ENABLED"
          : "DISABLED";
      });

    const payload = {
      location_ref_id: locId,
      storeTogglePlatformStatus,
      turn_on_at: null,
    };

    pauseOnlineOrderRestaurantV2(payload)
      .then((res) => {
        if (res?.data?.data?.es > 0) {
          showSnackbar(res?.data?.data?.message || "Unable to update platform status", "error");
          setPauseOrderLoader(null);
          onClose();
          return;
        }
        setActivePlatforms(enabledPlatforms);
        showSnackbar("Platform status updated successfully", "success");
        setPauseOrderLoader(null);
        onClose();
      })
      .catch((err) => {
        showSnackbar(err?.message || "Unable to update platform status", "error");
        setPauseOrderLoader(null);
      });
  };

  const isAllPaused = availablePlatforms?.length > 0 && selectedPlatforms?.length === 0;

  const handlePauseAllToggle = () => {
    if (isAllPaused) {
      setSelectedPlatforms([...availablePlatforms]);
    } else {
      setSelectedPlatforms([]);
    }
  };

  const handlePlatformToggle = (platformValue) => {
    if (!availablePlatforms?.includes(platformValue)) return;

    setSelectedPlatforms((prev) =>
      prev?.includes(platformValue)
        ? prev?.filter((v) => v !== platformValue)
        : [...prev, platformValue]
    );
  };

  const handleApplyPlatforms = () => {
    updatePlatformStatus(selectedPlatforms, "apply");
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={pauseModalStyle}>
        <div className="pause-orders-drawer">
          {/* Header */}
          <div className="drawer-header">
            <div>
              <h2 className="drawer-title">Pause Online Orders</h2>
            </div>
            <button
              className="drawer-close-btn"
              data-testid="button-close-pause-orders-drawer"
              onClick={onClose}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="drawer-content">
            {/* Pause All Banner */}
            <div className="pause-all-banner">
              <div className="pause-all-flex">
                <div>
                  <p className="pause-all-title">Pause All Channels</p>
                  <p className="pause-all-subtitle">Stop receiving orders from all sources</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isAllPaused ? "true" : "false"}
                  data-state={isAllPaused ? "checked" : "unchecked"}
                  onClick={handlePauseAllToggle}
                  className="drawer-toggle-switch"
                >
                  <span
                    data-state={isAllPaused ? "checked" : "unchecked"}
                    className="drawer-toggle-thumb"
                  />
                </button>
              </div>
            </div>

            {/* Platforms List */}
            <div className="platforms-list-container">
              {platformOptions
                ?.filter((p) => availablePlatforms?.includes(p?.value))
                ?.map((platform) => {
                  const isEnabled = selectedPlatforms?.includes(platform?.value);
                  const isPending = platformSyncStatus?.[platform?.value] === "PENDING";

                  return (
                    <div key={platform?.value} className="platform-row-item">
                      <div className="platform-info-flex">
                        <div className="platform-logo-box">{platform?.icon}</div>
                        <label
                          className="platform-label"
                          htmlFor={`platform-${platform?.value}`}
                        >
                          {platform?.label}
                        </label>
                      </div>
                      {isPending ? (
                        <span className="platform-waiting-label">Waiting</span>
                      ) : (
                        <button
                          type="button"
                          role="switch"
                          aria-checked={isEnabled ? "true" : "false"}
                          data-state={isEnabled ? "checked" : "unchecked"}
                          onClick={() => handlePlatformToggle(platform?.value)}
                          className="drawer-toggle-switch"
                        >
                          <span
                            data-state={isEnabled ? "checked" : "unchecked"}
                            className="drawer-toggle-thumb"
                          />
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Sticky Footer */}
          <div className="drawer-footer">
            <div className="drawer-footer-actions">
              <button
                className="drawer-btn-cancel"
                data-testid="button-cancel-pause-orders"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="drawer-btn-save"
                data-testid="button-save-pause-orders"
                onClick={handleApplyPlatforms}
                disabled={pauseOrderLoader === "apply"}
              >
                {pauseOrderLoader === "apply" ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default PauseOnlineOrdersModal;
